import { Link } from "react-router-dom";
import { Heart, MessageCircle, Clock, Pin, Lock } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { fileUrl } from "@/lib/api";
import { formatDistanceToNow } from "date-fns";
import ReportButton from "@/components/ReportButton";
import TesterBadge from "@/components/TesterBadge";
import { useAuth } from "@/context/AuthContext";

const CAT_LABELS = {
  recipes: "Recipes",
  decorating: "Decorating",
  "business-tips": "Business",
  "show-and-tell": "Show & Tell",
  troubleshooting: "Troubleshoot",
  "testers-chat": "Tester Chat",
};

export default function PostCard({ post, onLike }) {
  const { user } = useAuth();
  const timeAgo = (() => {
    try {
      return formatDistanceToNow(new Date(post.created_at), { addSuffix: true });
    } catch {
      return "";
    }
  })();

  return (
    <article className="cp-card p-5 sm:p-6" data-testid={`post-card-${post.id}`}>
      <div className="flex items-start gap-3 mb-3">
        <Avatar className="w-10 h-10 border border-[#EBDCD1]">
          <AvatarImage src={fileUrl(post.author.avatar_url)} />
          <AvatarFallback className="bg-[#FFD07F] text-[#3B2C24] font-semibold text-xs">
            {post.author.username?.slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/u/${post.author.username}`} className="font-semibold text-sm text-[#3B2C24] hover:underline">
              @{post.author.username}
            </Link>
            {post.author?.is_tester && <TesterBadge size="xs" />}
            <span className="cp-tag">{CAT_LABELS[post.category] || post.category}</span>
            {post.is_pinned && (
              <span className="cp-tag" style={{ background: "rgba(255, 126, 103, 0.18)" }} data-testid={`pinned-badge-${post.id}`}>
                <Pin className="w-3 h-3" /> Pinned
              </span>
            )}
            {post.is_locked && (
              <span className="cp-tag" style={{ background: "rgba(59, 44, 36, 0.10)" }} data-testid={`locked-badge-${post.id}`}>
                <Lock className="w-3 h-3" /> Locked
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 text-xs text-[#857163] mt-0.5">
            <Clock className="w-3 h-3" /> {timeAgo}
          </div>
        </div>
      </div>

      <Link to={`/p/${post.id}`} className="block group" data-testid={`post-title-${post.id}`}>
        <h3 className="font-heading font-semibold text-xl sm:text-2xl text-[#3B2C24] leading-tight group-hover:text-[#FF7E67] transition">
          {post.title}
        </h3>
      </Link>
      <p className="mt-2 text-[#857163] text-sm sm:text-base line-clamp-3">{post.body}</p>

      {post.image_url && (
        <Link to={`/p/${post.id}`} className="block mt-4">
          <img
            src={fileUrl(post.image_url)}
            alt=""
            className="w-full max-h-[400px] object-cover rounded-2xl border border-[#EBDCD1]"
          />
        </Link>
      )}

      {post.tags?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {post.tags.map((t) => (
            <span key={t} className="cp-tag">#{t}</span>
          ))}
        </div>
      )}

      <div className="mt-4 flex items-center gap-4 pt-3 border-t border-[#EBDCD1]">
        <button
          onClick={() => onLike?.(post)}
          className={`flex items-center gap-1.5 text-sm font-medium transition ${
            post.liked_by_me ? "text-[#FF7E67]" : "text-[#857163] hover:text-[#FF7E67]"
          }`}
          data-testid={`post-like-${post.id}`}
        >
          <Heart className={`w-4 h-4 ${post.liked_by_me ? "fill-[#FF7E67]" : ""}`} />
          <span>{post.like_count}</span>
        </button>
        <Link
          to={`/p/${post.id}`}
          className="flex items-center gap-1.5 text-sm text-[#857163] hover:text-[#3B2C24] transition"
          data-testid={`post-comments-${post.id}`}
        >
          <MessageCircle className="w-4 h-4" />
          <span>{post.comment_count}</span>
        </Link>
        {user && user !== false && user.id !== post.author.id && (
          <div className="ml-auto">
            <ReportButton postId={post.id} />
          </div>
        )}
      </div>
    </article>
  );
}
